import { morph } from '@theme/morph';

/**
 * Cart drawer behaviour.
 *
 * Extracted from snippets/cart-drawer.liquid on 2026-08-12. The drawer renders in
 * the header on EVERY page, so this was 10,524 bytes of uncached JS in every
 * document. Only two values needed Liquid; they now arrive as data attributes on
 * the <dialog>, which is what let the rest move out.
 *
 * Three functions stay on `window` because five other files call them:
 * snippets/cart-toast.liquid, snippets/cart-quantity-guard.liquid,
 * sections/main-product.liquid, sections/custom-header.liquid and
 * assets/custom-product-card.js. All of those call at click time, well after this
 * module has executed, so there is no load-order race.
 */

const DRAWER_ID = 'CartDrawer';

/** @returns {HTMLDialogElement | null} */
const getDrawer = () => /** @type {HTMLDialogElement | null} */ (document.getElementById(DRAWER_ID));

/** Config the server put on the dialog, replacing the two Liquid values. */
function config() {
  const drawer = getDrawer();
  return {
    currency: drawer?.dataset.currency || 'DKK',
    cartUpdateUrl: drawer?.dataset.cartUpdateUrl || '/cart/update',
  };
}

/**
 * Announce a cart change to screen readers.
 *
 * The live region lives inside the dialog on purpose: showModal() makes the rest
 * of the document inert, so a region outside it would never be read out.
 */
function announce(message) {
  const region = getDrawer()?.querySelector('[data-cd-status]');
  if (region) region.textContent = message;
}

/* ── Open ─────────────────────────────────────────────────────────────────── */

window.openCartDrawer = function (event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  const drawer = getDrawer();
  if (drawer && !drawer.open) drawer.showModal();

  /* GA4: view_cart — user-initiated open only, with fresh cart data. */
  if (window.gtmPush && window.gtmFetchCartEcommerce) {
    window.gtmFetchCartEcommerce().then((ecommerce) => {
      window.gtmPush({ event: 'view_cart', ecommerce });
    });
  }
};

/* Close on backdrop click. Bound to the dialog itself, which morph never
   replaces, so this survives every refresh. */
const drawerEl = getDrawer();
if (drawerEl) {
  drawerEl.addEventListener('click', (event) => {
    if (event.target === drawerEl) drawerEl.close();
  });
}

/* Auto-open after an add-to-cart that fell back to a full page reload. */
try {
  if (sessionStorage.getItem('openCartDrawer') === '1') {
    sessionStorage.removeItem('openCartDrawer');
    getDrawer()?.showModal();
  }
} catch (error) {
  /* Private mode — the drawer simply does not auto-open. */
}

/* ── Refresh ──────────────────────────────────────────────────────────────── */

/**
 * Re-render the drawer from the Section Rendering API.
 *
 * This used to do `drawer.innerHTML = fresh.innerHTML`, which threw away the
 * whole subtree on every +/- click. That reset the scroll position, closed any
 * open accordion, and — because the note textarea was replaced while its input
 * listener stayed bound to the old element — permanently killed cart-note
 * auto-saving after the first quantity change.
 *
 * morph() patches the existing nodes in place instead, so element identity (and
 * therefore scroll position, focus, and <details> open state) survives. It also
 * handles textarea and input values specifically, so a half-typed note is kept.
 */
window.refreshCartDrawer = function (options) {
  const shouldOpen = Boolean(options && options.open);
  const sectionId =
    document.querySelector('#' + DRAWER_ID)?.closest('.shopify-section')?.id?.replace('shopify-section-', '') ||
    'custom-header';

  const failOver = () => {
    try {
      sessionStorage.setItem('openCartDrawer', shouldOpen ? '1' : '0');
    } catch (error) {
      /* ignore */
    }
    window.location.reload();
  };

  fetch('/?sections=' + sectionId + ',custom-header')
    .then((response) => response.json())
    .then((sections) => {
      const html = sections[sectionId] || sections['custom-header'];
      if (!html) return failOver();

      /* DOMParser rather than innerHTML on a detached div — the pattern
         assets/section-renderer.js already uses for section responses. */
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('#' + DRAWER_ID);
      const current = getDrawer();
      if (!fresh || !current) return;

      const wasOpen = current.open;
      const scroller = current.querySelector('.cd__items');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      morph(current, fresh);

      /* morph preserves the node, but the list may have shrunk under the
         previous offset — clamp rather than assume. */
      const scrollerAfter = current.querySelector('.cd__items');
      if (scrollerAfter) {
        scrollerAfter.scrollTop = Math.min(scrollTop, scrollerAfter.scrollHeight);
      }

      const count = parsed.querySelector('.ch__cart-bubble')?.textContent.trim();
      if (count) {
        for (const bubble of document.querySelectorAll('.ch__cart-bubble')) {
          bubble.textContent = count;
        }
      }

      const subtotal = current.querySelector('.cd__subtotal-value')?.textContent.trim();
      announce([count ? count + ' ' : '', subtotal || ''].join('').trim());

      /* the recs block is re-rendered by the morph above, so re-run the upgrade */
      enhanceRecommendations();
      loadVenorUpsell();

      if (shouldOpen || wasOpen) current.showModal();
    })
    .catch(failOver);
};

/* ── Notices ──────────────────────────────────────────────────────────────────
   Shopify's own message, shown where the shopper is looking. The drawer had no
   error element at all, so a rejected quantity change was completely silent.
   Survives the morph because it is re-created on each failure. */

function clearNotice() {
  lastNotice = '';
  getDrawer()?.querySelector('[data-cd-notice]')?.remove();
}

let lastNotice = '';

function showNotice(message) {
  const drawer = getDrawer();
  if (!drawer || !message) return;
  /* clear first: clearNotice() also resets lastNotice, so setting it before
     this call would immediately wipe the value we are about to need. */
  clearNotice();
  lastNotice = message;

  const el = document.createElement('p');
  el.className = 'cd__notice';
  el.setAttribute('data-cd-notice', '');
  el.setAttribute('role', 'status');
  el.textContent = message;

  const items = drawer.querySelector('.cd__items');
  if (items) items.prepend(el);
  announce(message);
}

/* ── Quantity ─────────────────────────────────────────────────────────────── */

const quantityTimers = {};

window.updateCartDrawerQty = function (key, quantity) {
  if (quantityTimers[key]) clearTimeout(quantityTimers[key]);

  const row = document.querySelector('.cd__item[data-key="' + key + '"]');

  /* Optimistic UI so the click feels instant; the refresh below is the truth. */
  if (quantity === 0 && row) {
    row.classList.add('is-removing');
    if (window.gtmPush) {
      window.gtmPush({
        event: 'remove_from_cart',
        ecommerce: {
          currency: config().currency,
          value: (parseFloat(row.dataset.itemPrice) || 0) * (parseInt(row.dataset.itemQuantity, 10) || 1),
          items: [
            {
              item_id: row.dataset.itemId,
              item_name: row.dataset.itemName,
              item_variant: row.dataset.itemVariant,
              price: parseFloat(row.dataset.itemPrice) || 0,
              quantity: parseInt(row.dataset.itemQuantity, 10) || 1,
            },
          ],
        },
      });
    }
  } else if (row) {
    row.classList.add('is-updating');
    const count = row.querySelector('.cd__qty-count');
    if (count && quantity > 0) count.textContent = String(quantity);
  }

  quantityTimers[key] = setTimeout(() => {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then(async (response) => {
        const payload = await response.json();

        /* A 422 still parses as JSON, so `response.ok` has to be checked
           explicitly. Without it the error body was treated as a cart, and
           `cart.item_count` — undefined — was written into the bubble.

           This is what made "+" on a last-in-stock item look broken: the
           number flicked up, silently reverted, and Shopify's perfectly good
           explanation ("Der blev kun føjet 1 vare til din indkøbskurv, fordi
           der ikke er flere tilgængelige") was discarded. Shopify already
           localises that string, so it is shown as-is rather than replaced
           with a guess of our own. */
        if (!response.ok) {
          showNotice(payload.description || payload.message || '');
          throw new Error('cart change rejected');
        }
        return payload;
      })
      .then((cart) => {
        clearNotice();
        for (const bubble of document.querySelectorAll('.ch__cart-bubble')) {
          bubble.textContent = cart.item_count;
        }
        window.refreshCartDrawer({ open: true });
      })
      .catch(() => {
        row?.classList.remove('is-updating', 'is-removing');
        /* The refresh morphs .cd__items, which throws away a notice prepended
           to it. So re-apply after the refresh settles rather than before —
           the first version of this fix showed the message and then deleted
           it a few hundred milliseconds later. */
        const pending = lastNotice;
        window.refreshCartDrawer({ open: true });
        if (pending) setTimeout(() => showNotice(pending), 900);
      });
  }, 150);
};

/* ── Delegated UI handlers ────────────────────────────────────────────────────
   These replace nine inline onclick attributes. Delegation is not a style
   preference here: a handler bound to a specific node dies the moment that node
   is re-rendered, which is exactly how the cart-note listener was lost. */

document.addEventListener('click', (event) => {
  const target = /** @type {Element} */ (event.target);

  const closer = target.closest?.('[data-cd-close]');
  if (closer) {
    /* Links keep navigating; the drawer just gets out of the way. */
    getDrawer()?.close();
    return;
  }

  const stepper = target.closest?.('[data-cd-qty]');
  if (stepper) {
    event.preventDefault();
    window.updateCartDrawerQty(
      stepper.getAttribute('data-cd-qty'),
      parseInt(stepper.getAttribute('data-cd-qty-to') || '0', 10)
    );
  }
});

/* ── Cart note ────────────────────────────────────────────────────────────────
   Delegated so it cannot be orphaned by a re-render again. morph now preserves
   the textarea too, but the listener must not depend on that. */

let noteTimer;
document.addEventListener('input', (event) => {
  const note = /** @type {HTMLTextAreaElement} */ (event.target);
  if (!note.matches?.('[data-cart-note]')) return;
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.value }),
    });
  }, 500);
});

/* ── Discount code ────────────────────────────────────────────────────────── */

function appliedDiscountCodes() {
  return Array.from(document.querySelectorAll('#' + DRAWER_ID + ' .cd__discount-pill'))
    .map((pill) => pill.getAttribute('data-discount-code'))
    .filter(Boolean);
}

function submitDiscounts(codes, applyButton) {
  if (applyButton) applyButton.disabled = true;
  return fetch(config().cartUpdateUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') }),
  })
    .then((response) => response.json())
    .finally(() => {
      if (applyButton) applyButton.disabled = false;
    });
}

document.addEventListener('submit', (event) => {
  const form = /** @type {Element} */ (event.target).closest?.('[data-cart-discount-form]');
  if (!form) return;
  event.preventDefault();

  const input = form.querySelector('input[name="discount"]');
  const error = document.querySelector('#' + DRAWER_ID + ' [data-cart-discount-error]');
  const applyButton = form.querySelector('.cd__discount-apply');
  if (!input || !input.value.trim()) return;

  const code = input.value.trim();
  const existing = appliedDiscountCodes();
  if (existing.includes(code)) {
    input.value = '';
    return;
  }
  error?.classList.add('hidden');

  submitDiscounts(existing.concat([code]), applyButton).then((data) => {
    const rejected = (data.discount_codes || []).some((d) => d.code === code && d.applicable === false);
    if (rejected) {
      error?.classList.remove('hidden');
      input.value = '';
      return;
    }
    window.refreshCartDrawer({ open: true });
  });
});

document.addEventListener('click', (event) => {
  const remove = /** @type {Element} */ (event.target).closest?.('[data-cart-discount-remove]');
  if (!remove) return;
  event.preventDefault();
  const code = remove.getAttribute('data-cart-discount-remove');
  const remaining = appliedDiscountCodes().filter((c) => c !== code);
  submitDiscounts(remaining, null).then(() => window.refreshCartDrawer({ open: true }));
});

/* ── Checkout ─────────────────────────────────────────────────────────────────
   The re-submit MUST pass the original submitter. Without it the button's
   name="checkout" never reaches Shopify and the cart loops back to /cart — the
   bug fixed in f7b95f6. Do not simplify this to form.submit(). */

document.addEventListener('submit', (event) => {
  const form = /** @type {Element} */ (event.target).closest?.('.cd__checkout-form');
  if (!form) return;
  if (!window.gtmPush || !window.gtmFetchCartEcommerce) return;
  if (form.dataset.gtmProceed === '1') return;

  event.preventDefault();
  const submitter = event.submitter;

  const proceed = () => {
    form.dataset.gtmProceed = '1';
    if (form.requestSubmit) {
      form.requestSubmit(submitter || undefined);
    } else if (submitter) {
      submitter.click();
    } else {
      form.submit();
    }
  };

  /* Never let analytics stand between a shopper and checkout. */
  let done = false;
  const go = () => {
    if (done) return;
    done = true;
    proceed();
  };
  setTimeout(go, 600);
  window.gtmFetchCartEcommerce()
    .then((ecommerce) => {
      window.gtmPush({ event: 'begin_checkout', ecommerce });
      go();
    })
    .catch(go);
});

/* ── Safety cross-sell: one-tap add ───────────────────────────────────────── */

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-cd-quick-add]') : null;
  if (!button) return;

  event.preventDefault();
  const id = button.getAttribute('data-cd-quick-add');
  if (!id || button.disabled) return;

  button.disabled = true;
  fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
  })
    .then((response) => {
      if (!response.ok) throw new Error('add failed');
      return window.refreshCartDrawer({ open: true });
    })
    .catch(() => {
      /* leave the row in place so the shopper can try again */
      button.disabled = false;
    });
});

/* ── Recommendations: upgrade the over-threshold list ─────────────────────────
   Gap mode is left alone — those picks are chosen to close the free-shipping
   shortfall, which the recommendations engine cannot do. Once the shopper is
   over the threshold there is no gap to close, so complementary products are a
   better answer than "popular". Order is complementary → related → whatever the
   server already rendered, and a result set smaller than three is treated as no
   result: a single stray suggestion looks worse than the collection row. */

const RECS_WANTED = 3;

async function fetchRecommendations(productId, intent) {
  const url =
    window.Shopify.routes.root +
    'recommendations/products.json?product_id=' +
    encodeURIComponent(productId) +
    '&limit=4&intent=' +
    intent;

  const response = await fetch(url);
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data.products) ? data.products : [];
}

function renderRecommendation(product) {
  const item = document.createElement('li');
  item.className = 'cfr__item';

  const link = document.createElement('a');
  link.className = 'cfr__link';
  link.href = product.url;
  link.setAttribute('data-cd-close', '');

  if (product.featured_image) {
    const image = document.createElement('img');
    image.className = 'cfr__img';
    image.src = product.featured_image;
    image.alt = product.title;
    image.loading = 'lazy';
    image.width = 44;
    image.height = 44;
    link.appendChild(image);
  }

  const text = document.createElement('span');
  text.className = 'cfr__text';

  const title = document.createElement('span');
  title.className = 'cfr__title';
  title.textContent = product.title;

  const price = document.createElement('span');
  price.className = 'cfr__price';
  /* price is in cents; the drawer already carries the shop's currency */
  price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'da', {
    style: 'currency',
    currency: getDrawer()?.getAttribute('data-currency') || 'DKK',
  }).format(product.price / 100);

  text.append(title, price);
  link.appendChild(text);
  item.appendChild(link);
  return item;
}

async function enhanceRecommendations() {
  const block = document.querySelector('[data-cd-recs]');
  if (!block || block.getAttribute('data-cd-recs-mode') === 'gap') return;
  if (block.hasAttribute('data-cd-recs-done')) return;

  const productId = block.getAttribute('data-cd-recs-product');
  const list = block.querySelector('.cd__recs-list');
  if (!productId || !list) return;

  try {
    let products = await fetchRecommendations(productId, 'complementary');
    if (products.length < RECS_WANTED) {
      products = await fetchRecommendations(productId, 'related');
    }
    if (products.length < RECS_WANTED) return; /* keep the server-rendered row */

    list.replaceChildren(...products.slice(0, 4).map(renderRecommendation));
    block.setAttribute('data-cd-recs-done', '');
  } catch {
    /* network or shape problem — the server-rendered row stays */
  }
}

enhanceRecommendations();

/* ── VENOR protection upsell ──────────────────────────────────────────────────
   The cart drawer offers the matching Reversal protection set when the cart
   holds VENOR skates. The markup comes from a section fetched at a tag-filtered
   collection URL, because Liquid cannot filter products by tag — the same route
   sections/product-group-swatches.liquid takes for colour groups.

   Sizes are addressed by position, never by label: those labels sync from TRAEDE
   and are translated per market, so "3-7 Years" is a different string on each
   storefront while its index is not. */

/* Versioned: a markup change must not be served from a copy cached by an
   earlier build. */
const VENOR_CACHE = 'scwVpp2:';

function venorApply(picker) {
  const swatch = picker.querySelector('[data-vpp-color][aria-pressed="true"]');
  const sizeButton = picker.querySelector('.vpp__size[aria-pressed="true"]');
  const nameEl = picker.querySelector('[data-vpp-name]');
  /* The lookup rows carry a price too, so the output element needs an attribute
     of its own rather than sharing one with the data it displays. */
  const priceEl = picker.querySelector('[data-vpp-price-out]');
  const addButton = picker.querySelector('[data-vpp-add]');
  if (!swatch || !addButton) return;

  const handle = swatch.getAttribute('data-vpp-color');

  for (const shot of picker.querySelectorAll('[data-vpp-shot]')) {
    shot.classList.toggle('vpp__shot--on', shot.getAttribute('data-vpp-shot') === handle);
  }
  /* The heading and subtitle already say what the product is, so the name line
     only names the colourway — one line instead of two in a narrow drawer. The
     full product name stays available to screen readers. */
  if (nameEl) nameEl.textContent = swatch.getAttribute('data-vpp-color-name') || '';

  /* Say what was chosen and why, so the card reads as a decision already made. */
  const matchedEl = picker.querySelector('[data-vpp-matched]');
  if (matchedEl) {
    const sizeOn = picker.querySelector('.vpp__size[aria-pressed="true"]');
    const bits = [];
    if (sizeOn) bits.push(sizeOn.textContent.trim());
    if (swatch.classList.contains('vpp__color--match')) {
      bits.push(picker.getAttribute('data-vpp-match-note') || '');
    }
    matchedEl.textContent = bits.filter(Boolean).join(' · ');
  }
  const fullName = picker.querySelector('[data-vpp-fullname]');
  if (fullName) fullName.textContent = swatch.getAttribute('data-vpp-product-name') || '';

  /* A colourway can be short of one age range, so the size buttons are re-tested
     against the chosen colour rather than assumed available. */
  for (const button of picker.querySelectorAll('.vpp__size')) {
    const index = button.getAttribute('data-vpp-size-index');
    const row = picker.querySelector(
      '[data-vpp-lookup="' + handle + '"][data-vpp-lookup-size="' + index + '"]'
    );
    button.disabled = !row || row.getAttribute('data-vpp-available') !== '1';
    if (button.disabled) button.setAttribute('aria-pressed', 'false');
  }

  const chosen =
    sizeButton && !sizeButton.disabled
      ? picker.querySelector(
          '[data-vpp-lookup="' + handle + '"][data-vpp-lookup-size="' +
            sizeButton.getAttribute('data-vpp-size-index') + '"]'
        )
      : null;

  /* The same markup serves two places. In the drawer it is a standalone offer
     with its own add button; inside the picker it is one half of a combined add,
     so there the choice is published to the mount and the picker owns the button. */
  const mount = picker.closest('[data-venor-mount]');
  const inline = mount ? mount.hasAttribute('data-venor-inline') : false;

  if (chosen) {
    const variantId = chosen.getAttribute('data-vpp-variant');
    const priceText = chosen.getAttribute('data-vpp-lookup-price') || '';
    if (priceEl) priceEl.textContent = priceText;

    if (inline) {
      mount.dataset.venorVariant = variantId;
      mount.dataset.venorPrice = priceText;
      mount.dispatchEvent(
        new CustomEvent('venor:addon', { bubbles: true, detail: { variantId, priceText } })
      );
    } else {
      /* Handing the variant id to the existing quick-add handler rather than
         posting here keeps one add path in this file. */
      addButton.setAttribute('data-cd-quick-add', variantId);
      addButton.disabled = false;
    }
  } else {
    addButton.removeAttribute('data-cd-quick-add');
    addButton.disabled = true;
    if (priceEl) priceEl.textContent = '';
    if (inline) {
      delete mount.dataset.venorVariant;
      mount.dispatchEvent(
        new CustomEvent('venor:addon', { bubbles: true, detail: { variantId: null } })
      );
    }
  }
}

function venorInit(mount) {
  const picker = mount.querySelector('[data-vpp]');
  if (!picker) return;

  /* Preselect the colourway that matches the skate already in the cart, when the
     set is made in that colour. Two of them are — Ocean and Chameleon Rainbow —
     so this is a real match rather than a hopeful one. */
  const wanted = (mount.getAttribute('data-venor-color-name') || '').trim().toLowerCase();
  const swatches = [...picker.querySelectorAll('[data-vpp-color]')];
  let chosenSwatch = swatches[0];
  if (wanted) {
    const match = swatches.find(
      (s) => (s.getAttribute('data-vpp-color-name') || '').trim().toLowerCase() === wanted
    );
    if (match) chosenSwatch = match;
    /* Marked as the match, not merely selected. The two drift apart the moment
       the shopper picks a different colour, and the label is only honest about
       the one that actually matches the skate. */
    for (const s of swatches) s.classList.toggle('vpp__color--match', s === match);
  }
  for (const s of swatches) {
    const on = s === chosenSwatch;
    s.classList.toggle('vpp__color--on', on);
    s.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  if (chosenSwatch) chosenSwatch.scrollIntoView({ block: 'nearest', inline: 'nearest' });

  /* Age range derived from the skate size the shopper already chose. */
  const sizeIndex = mount.getAttribute('data-venor-size-index') || '0';
  const sizeButtons = picker.querySelectorAll('.vpp__size');
  for (const button of sizeButtons) {
    button.setAttribute(
      'aria-pressed',
      button.getAttribute('data-vpp-size-index') === sizeIndex ? 'true' : 'false'
    );
  }

  venorApply(picker);

  /* The drawer footer is tall, so on a short list the add button can land just
     below the fold — which is not much of a one-tap offer. Nudge the pane by the
     minimum needed. This runs immediately after the shopper tapped "add", while
     the pane is still at the top, so it reads as the drawer settling rather than
     as content moving under them. */
  requestAnimationFrame(() => {
    const pane = picker.closest('.cd__items');
    const rect = picker.getBoundingClientRect();
    if (!pane) return;
    const paneRect = pane.getBoundingClientRect();
    const overshoot = rect.bottom - paneRect.bottom;
    if (overshoot > 0) pane.scrollTop += overshoot + 8;
  });
}

/* The picker calls this when the shopper changes skate colour or size, so the
   set follows the skate rather than staying on whatever it matched first. */
window.venorSyncUpsell = function (mount) {
  if (mount && mount.querySelector('[data-vpp]')) venorInit(mount);
};

function venorFill(mount, markup) {
  if (!markup) return;
  mount.innerHTML = markup;
  venorInit(mount);
}

function loadVenorUpsell() {
  for (const mount of document.querySelectorAll('[data-venor-mount]')) {
    /* The drawer is morphed in place on every refresh, so a mount can come back
       flagged but empty. Refilling is cheap and idempotent; showing nothing is not. */
    if (mount.hasAttribute('data-venor-loaded') && mount.firstElementChild) continue;
    mount.setAttribute('data-venor-loaded', '');

    const tag = mount.getAttribute('data-venor-tag');
    if (!tag) continue;

    /* Keyed by the theme's current asset version, not just its id. The id does not
       change when the theme is redeployed, so a shopper part-way through a session
       kept the previous markup — which is exactly how an English age label
       survived a push that had already fixed it. The stamp comes from the mount,
       where Liquid can see the asset URL's version. */
    const stamp = mount.getAttribute('data-venor-cache') || '0';
    const key = VENOR_CACHE + stamp + ':' + tag;
    let cached = null;
    try {
      cached = sessionStorage.getItem(key);
    } catch {
      /* private mode — fall through to the network */
    }
    if (cached !== null) {
      venorFill(mount, cached);
      continue;
    }

    const root = window.Shopify?.routes?.root || '/';
    fetch(root + 'collections/all/' + encodeURIComponent(tag) + '?section_id=venor-protection-picker')
      .then((response) => {
        if (!response.ok) throw new Error('vpp ' + response.status);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('.vpp');
        const markup = section ? section.outerHTML : '';
        try {
          sessionStorage.setItem(key, markup);
        } catch {
          /* quota or private mode — the cache is an optimisation, not a requirement */
        }
        venorFill(mount, markup);
      })
      .catch(() => {
        /* Leave the slot empty; the cart is fully usable without the upsell. */
      });
  }
}

/**
 * Close the protection picker's colour-and-size panel.
 *
 * @param {Element | null | undefined} picker - The [data-vpp] root.
 * @param {{keepFocus?: boolean}} [options] - keepFocus leaves focus alone, for
 *   closes the shopper did not ask for by name — clicking elsewhere on the page
 *   should not yank the caret back to this card.
 */
function closeVppEdit(picker, options) {
  const panel = picker?.querySelector('.vpp__edit');
  const toggle = picker?.querySelector('[data-vpp-toggle]');
  if (!panel || !toggle || panel.hidden) return;
  panel.hidden = true;
  toggle.setAttribute('aria-expanded', 'false');
  if (!options?.keepFocus) toggle.focus();
}

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  for (const picker of document.querySelectorAll('[data-vpp]')) closeVppEdit(picker);
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  /* The panel is collapsed by default: the colour and the age are worked out
     from the skate, so most shoppers confirm rather than choose. */
  const toggle = target.closest('[data-vpp-toggle]');
  if (toggle) {
    const picker = toggle.closest('[data-vpp]');
    const panel = picker?.querySelector('.vpp__edit');
    if (!panel) return;
    const opening = panel.hidden;
    panel.hidden = !opening;
    toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
    if (opening) {
      const current = panel.querySelector('[aria-pressed="true"]');
      (current || panel.querySelector('button'))?.focus();
    }
    return;
  }

  /* Done, or a click anywhere outside. Choosing no longer closes the panel:
     colour and size live together now, and closing after the first pick is
     exactly what used to make the second one a hunt. */
  const done = target.closest('[data-vpp-done]');
  if (done) {
    closeVppEdit(done.closest('[data-vpp]'));
    return;
  }

  for (const picker of document.querySelectorAll('[data-vpp]')) {
    if (!picker.contains(target)) closeVppEdit(picker, { keepFocus: true });
  }

  const swatch = target.closest('[data-vpp-color]');
  if (swatch) {
    const picker = swatch.closest('[data-vpp]');
    if (!picker) return;
    for (const s of picker.querySelectorAll('[data-vpp-color]')) {
      const on = s === swatch;
      s.classList.toggle('vpp__color--on', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    /* Deliberately does NOT close the panel — the shopper may well want to
       change the size next, and closing here is what used to cost them a
       second trip through the menu. */
    venorApply(picker);
    return;
  }

  const size = target.closest('.vpp__size');
  if (size && !size.disabled) {
    const picker = size.closest('[data-vpp]');
    if (!picker) return;
    for (const b of picker.querySelectorAll('.vpp__size')) {
      b.setAttribute('aria-pressed', b === size ? 'true' : 'false');
    }
    venorApply(picker);
  }
});

loadVenorUpsell();
